// ═══════════════════════════════════════════════════════════════════════════
//  POTONGAN TAMPILAN REMOTE TAMU — dipakai Remote.tsx & RemoteJelajah.tsx
//  (5 Okt 2026). Sampul lagu, equalizer, kerangka memuat.
//  Gerak (kelas rm-*) ada di index.css dan menghormati prefers-reduced-motion.
// ═══════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react'
import { sampul } from '../lib/tamu'

export const WARNA_KARTU = [
  'from-rose-500/70 to-orange-500/50', 'from-violet-500/70 to-indigo-500/50',
  'from-emerald-500/70 to-teal-500/50', 'from-sky-500/70 to-blue-600/50',
  'from-amber-400/70 to-yellow-600/50', 'from-pink-500/70 to-fuchsia-600/50',
]

export function inisial(s: string) {
  return s.split(/\s+/).slice(0, 2).map(x => x[0]?.toUpperCase() ?? '').join('')
}

// `kodeRoom` diset sekali oleh layar remote — komponen kecil di mana pun
// tidak perlu mengoper kode room lewat lima lapis props.
let kodeRoom = ''
export const setKodeRoom = (k: string) => { kodeRoom = k }

/** Sampul: gambar yang sudah diketahui (Spotify/YouTube) → iTunes lewat
 *  server (disinggahkan) → gradasi + inisial artis. */
export function Sampul({ judul, artis, warna, className, teks, gambar }: {
  judul: string; artis: string; warna: string; className?: string; teks?: string
  gambar?: string
}) {
  const [url, setUrl] = useState<string>(gambar ?? '')
  const [rusak, setRusak] = useState(false)
  useEffect(() => {
    let hidup = true
    setRusak(false)
    if (gambar) { setUrl(gambar); return }
    setUrl('')
    if (kodeRoom) sampul(kodeRoom, judul, artis).then(u => { if (hidup) setUrl(u) })
    return () => { hidup = false }
  }, [judul, artis, gambar])
  // 27 Okt 2026 — Zaldy (Huawei tab "berat, lemot"; laptop aman): sampul
  // iTunes datang 600×600 px padahal kotaknya 48–68 px. Seratus baris = seratus
  // gambar 600×600 didekode tablet murah. Ukuran di URL iTunes bisa diganti
  // (…/600x600bb.jpg → …/160x160bb.jpg) — sekali retina tetap tajam.
  const kecil = url ? url.replace(/\/\d{2,4}x\d{2,4}bb(\.|-)/, '/160x160bb$1') : ''
  return (
    <span className={`relative overflow-hidden bg-gradient-to-br ${warna} grid place-items-center shrink-0 ${className ?? ''}`}>
      <span className="font-semibold text-white/85 drop-shadow">{teks ?? inisial(artis || judul)}</span>
      {kecil && !rusak && (
        <img src={kecil} alt="" className="absolute inset-0 w-full h-full object-cover rm-pudar" loading="lazy" decoding="async"
          onError={() => setRusak(true)} />
      )}
    </span>
  )
}

// ── MODE RINGAN UNTUK TABLET LEMAH (27 Okt 2026) ────────────────────────────
// Tablet kamar murah (Huawei MatePad SE dsb., WebView lama, 2–4 inti): animasi
// muncul bertahap, bayangan, dan gradasi di ratusan baris membuat gulir
// tersendat. Di perangkat seperti itu <html> diberi kelas `ringan`; index.css
// mematikan animasi/bayangan/kaca buram. Laptop & HP cepat tidak berubah.
// Bisa dipaksa untuk uji: ?ringan=1 / ?ringan=0 (diingat di localStorage).
export function pasangModeRingan() {
  try {
    const q = new URLSearchParams(location.search).get('ringan')
    if (q === '1' || q === '0') localStorage.setItem('karaoke.remote.ringan', q)
    const simpan = localStorage.getItem('karaoke.remote.ringan')
    const n = navigator as Navigator & { deviceMemory?: number }
    const ua = navigator.userAgent
    const lemah = simpan === '1' || (simpan !== '0' && (
      (n.hardwareConcurrency ?? 8) <= 4 ||
      (n.deviceMemory ?? 8) <= 4 ||
      /HUAWEI|HarmonyOS|HMSCore|MatePad|Android [4-9]\./i.test(ua)))
    document.documentElement.classList.toggle('ringan', lemah)
    return lemah
  } catch { return false }
}

/** Equalizer beranimasi untuk lagu yang sedang diputar. */
export function Ekualiser({ diam = false, className = '' }: { diam?: boolean; className?: string }) {
  return (
    <span className={`rm-eq ${diam ? 'rm-eq-diam' : ''} ${className}`} aria-hidden>
      <span /><span /><span /><span />
    </span>
  )
}

/** Kerangka baris lagu saat memuat. */
export function KerangkaBaris({ n = 6 }: { n?: number }) {
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      {Array.from({ length: n }, (_, i) => (
        <div key={i} className="flex items-center gap-3 rounded-2xl border border-white/[0.06] bg-white/[0.025] px-3 py-2.5">
          <span className="rm-kerangka w-12 h-12 rounded-xl shrink-0" />
          <span className="flex-1 space-y-2">
            <span className="rm-kerangka block h-3 rounded-full w-3/4" />
            <span className="rm-kerangka block h-2.5 rounded-full w-1/2" />
          </span>
        </div>
      ))}
    </div>
  )
}
